import React, { useState, useRef, } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Navbar, Footer, Breadcrumbs } from "../../components/shared";
import { handleCreateFamily } from "../../service/FamilyApi";
import '../general/General.css';
import { businessNames, customGroups } from '../general/DropDownValuesConfig';
import { fetchAddressAreaByPin } from "../../service/AddressApi";
import { useCheckAccess } from "../../utils/Utilities";
import { translateText } from "../../service/UtilApi";
import "react-datepicker/dist/react-datepicker.css";
import { useForm } from "react-hook-form";
import { formatDateToISO, getYearOptions } from "../../utils/Utilities";

const FamilyCreate = () => {
  useCheckAccess('FamilyRW'); // Check if the user is logged in (if no user data, redirect to login)
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const dateInputRef = useRef(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [familyMembers, setFamilyMembers] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    watch,
    trigger,
    clearErrors,
    setValue
  } = useForm({
    mode: "all",
    shouldUnregister: false,
  });

  // Address logic - start
  const [addressOption, setAddressOption] = useState("both");
  const [permanentSelectedAreaId, setPermanentSelectedAreaId] = useState(null);
  const [permanentSelectedSubareaId, setPermanentSelectedSubareaId] = useState(null);
  const [permanentAddressAreas, setPermanentAddressAreas] = useState({});
  const [permanentPincode, setPermanentPincode] = useState('');
  const [tempSelectedAreaId, setTempSelectedAreaId] = useState(null);
  const [tempSelectedSubareaId, setTempSelectedSubareaId] = useState(null);
  const [tempAddressAreas, setTempAddressAreas] = useState({});
  const [tempPincode, setTempPincode] = useState('');
  const [permanentErrorMessage, setPermanentErrorMessage] = useState('');
  const [tempErrorMessage, setTempErrorMessage] = useState('');

  const [loadingPermanentPin, SetLoadingPermanentPin] = useState(false);
  const [loadingTempPin, SetLoadingTempPin] = useState(false);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);

    const dateString = `${year}-06-01`;

    // Update both DOM and react-hook-form state
    if (dateInputRef.current) {
      dateInputRef.current.value = dateString;
    }
    setValue("dob", dateString, { shouldValidate: true });
  };

  // Handle the area selection
  const handleAreaChange = (e) => {
    const selectedId = e.target.value;
    setPermanentSelectedAreaId(selectedId);
    setPermanentSelectedSubareaId(null);
  };
  const handleTempAreaChange = (e) => {
    const selectedId = e.target.value;
    setTempSelectedAreaId(selectedId);
    setTempSelectedSubareaId(null);
  };


  // Handle subarea selection
  const handleSubareaChange = (e) => {
    const selectedId = e.target.value;
    setPermanentSelectedSubareaId(selectedId);
  };
  const handleTempSubareaChange = (e) => {
    const selectedId = e.target.value;
    setTempSelectedSubareaId(selectedId);
  };

  // Load address areas based on the entered pincode
  const loadPermanentAddressAreas = async () => {
    if (!permanentPincode.match(/^\d{6}$/)) {
      setPermanentErrorMessage("6 இலக்க பின்கோடை உள்ளிடவும்");
      return;
    }

    if (permanentPincode !== permanentAddressAreas?.pincode) {
      // Only reset selections if user entered a different pincode
      setPermanentSelectedAreaId(null);
      setPermanentSelectedSubareaId(null);
    }

    SetLoadingPermanentPin(true);
    try {
      const data = await fetchAddressAreaByPin(permanentPincode, 'true');
      if (data?.addressAreaList?.length) {
        setPermanentAddressAreas({ ...data, permanentPincode }); // Save current pincode in state
        setPermanentErrorMessage("");
      } else {
        setPermanentAddressAreas({});
        setPermanentErrorMessage('இந்த பின்கோடுக்கு முகவரிப் பகுதிகள் எதுவும் கிடைக்கவில்லை.');
      }
    } catch (error) {
      setPermanentErrorMessage("முகவரிப் பகுதிகளைப் பெறுவதில் பிழை.");
    } finally {
      SetLoadingPermanentPin(false);
    }
  };
  const loadTempAddressAreas = async () => {
    if (!tempPincode.match(/^\d{6}$/)) {
      setTempErrorMessage("6 இலக்க பின்கோடை உள்ளிடவும்");
      return;
    }

    if (tempPincode !== tempAddressAreas?.pincode) {
      // Only reset selections if user entered a different pincode
      setTempSelectedAreaId(null);
      setTempSelectedSubareaId(null);
    }

    SetLoadingTempPin(true);
    try {
      const data = await fetchAddressAreaByPin(tempPincode, 'true');
      if (data?.addressAreaList?.length) {
        setTempAddressAreas({ ...data, tempPincode }); // Save current pincode in state
        setTempErrorMessage("");
      } else {
        setTempAddressAreas({});
        setTempErrorMessage('இந்த பின்கோடுக்கு முகவரிப் பகுதிகள் எதுவும் கிடைக்கவில்லை.');
      }
    } catch (error) {
      setTempErrorMessage("முகவரிப் பகுதிகளைப் பெறுவதில் பிழை.");
    } finally {
      SetLoadingTempPin(false);
    }
  };
  // Address logic - end
  const [nameData, setNameData] = useState({
    tempFirstName: "",
    tempLastName: "",
    tempFamilyName: "",
    tempFamilyOrigin: "",
    firstName: "",
    lastName: "",
    familyName: "",
    familyOrigin: "",
  });

  const handleNameChange = (field, value) => {
    setNameData((prevData) => ({
      ...prevData,
      [field]: value, // Dynamically set the correct field
    }));
  };

  // Function to add new member
  const addFamilyMember = () => {
    setFamilyMembers([...familyMembers, { mobile: '', mobile2: '', aadhar: '' }]);
  };

  // Function to update member fields
  const updateFamilyMember = (index, field, value) => {
    const updatedMembers = [...familyMembers];
    updatedMembers[index][field] = value;
    setFamilyMembers(updatedMembers);
  };
  const removeFamilyMember = (index) => {
    const updatedMembers = [...familyMembers];
    updatedMembers.splice(index, 1);
    setFamilyMembers(updatedMembers);
  };

  // Translate the first name to Tamil
  const translateNameToTamil = async () => {
    try {
      if (nameData.tempFirstName) {
        const translatedFirstName = await translateText(nameData.tempFirstName);
        setNameData((prevData) => ({
          ...prevData,
          firstName: translatedFirstName,
        }));
      }
      if (nameData.tempLastName) {
        const translatedLastName = await translateText(nameData.tempLastName);
        setNameData((prevData) => ({
          ...prevData,
          lastName: translatedLastName,
        }));
      }
      if (nameData.tempFamilyName) {
        const translatedFamilyName = await translateText(nameData.tempFamilyName);
        setNameData((prevData) => ({
          ...prevData,
          familyName: translatedFamilyName,
        }));
      }
      if (nameData.tempFamilyOrigin) {
        const translatedFamilyOrigin = await translateText(nameData.tempFamilyOrigin);
        setNameData((prevData) => ({
          ...prevData,
          familyOrigin: translatedFamilyOrigin,
        }));
      }
    } catch (error) {
      console.error("Error translating name:", error);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (data) => {
    if (loading) {
      return;  // Prevent form submission if loading is true
    }
    if (!nameData.firstName || nameData.firstName.length < 3) {
      alert("பெயர் சரியாக உள்ளிடப்படவில்லை.");
      return;
    }
    setLoading(true);
    // Clean the Aadhaar field specifically (remove spaces)
    if (data.aadhar) {
      data.aadhar = data.aadhar.replace(/\s+/g, ''); // Remove all spaces from Aadhaar
    }
    if (data.aadhar && (data.aadhar.startsWith("TMP") || data.aadhar.startsWith("00"))) {
      data.status = "draft";
    }
    else {
      data.status = "active";
    }
    if (data.status === "draft" && data.applicationFormNumber === "0000") {
      data.applicationFormNumber = "";
    }
    // Check if permanent address area and subarea are filled
    if (!permanentSelectedAreaId || !permanentSelectedSubareaId) {
      alert("தயவுசெய்து நிரந்தர முகவரியை நிரப்பவும்.");
      setLoading(false);
      return; // Prevent form submission if validation fails
    }
    const duplicateErrors = validateFamilyDuplicates(data, familyMembers || []);
    if (duplicateErrors.length > 0) {
      alert("Validation Error:\n" + duplicateErrors.join("\n"));
      setLoading(false);
      return;
    }

    const payload = {
      ...data,
      dob: formatDateToISO(data.dob),
      firstName: (nameData.firstName?.trim() || ""), // Safely trim the firstName or fall back to empty string
      lastName: (nameData.lastName?.trim() || ""),   // Safely trim lastName or fall back to empty string
      familyName: (nameData.familyName?.trim() || ""), // Safely trim familyName or fall back to empty string
      familyOrigin: (nameData.familyOrigin?.trim() || ""),
      applicationFormNumber: data.applicationFormNumber,
      tempAddressLine1: (data.tempAddressLine1?.trim() || ""), // Safely trim tempAddressLine1 or fall back to empty string
      tempAddressLine2: (data.tempAddressLine2?.trim() || ""),
      tempAddressLine3: (data.tempAddressLine3?.trim() || ""),
      tempAddressLine1_tamil: (data.tempAddressLine1?.trim() || ""), // Same for Tamil lines
      tempAddressLine2_tamil: (data.tempAddressLine2?.trim() || ""),
      tempAddressLine3_tamil: (data.tempAddressLine3?.trim() || ""),
      tempAddressAreaId: tempSelectedAreaId || "", // Default to empty string if areaId is undefined
      tempSubareaId: tempSelectedSubareaId || "", // Default to empty string if subareaId is undefined
      permanentAddressLine1: (data.permanentAddressLine1?.trim() || ""), // Safely trim permanent address lines
      permanentAddressLine2: (data.permanentAddressLine2?.trim() || ""),
      permanentAddressLine3: (data.permanentAddressLine3?.trim() || ""),
      permanentAddressLine1_tamil: (data.permanentAddressLine1?.trim() || ""),
      permanentAddressLine2_tamil: (data.permanentAddressLine2?.trim() || ""),
      permanentAddressLine3_tamil: (data.permanentAddressLine3?.trim() || ""),
      permanentAddressAreaId: permanentSelectedAreaId || "", // Default to empty string if areaId is undefined
      permanentSubareaId: permanentSelectedSubareaId || "", // Default to empty string if subareaId is undefined
      familyMembers,
    };

    try {
      const familyId = await handleCreateFamily(payload);
      if (familyId) {
        navigate(`/family/view/${familyId}`);
      }
    } catch (error) {
      console.error("Error creating family:", error);
    } finally {
      setLoading(false);
    }
  };


  const validateFamilyDuplicates = (mainData, familyMembers = []) => {
    const mobileSet = new Set();
    const aadhaarSet = new Set();
    const errors = [];

    const normalize = (value) => (value ? String(value).trim() : null);

    const addMobile = (value, label) => {
      const val = normalize(value);
      if (!val) return;
      if (mobileSet.has(val)) {
        errors.push(`Duplicate mobile number found: ${label} (${val})`);
      } else {
        mobileSet.add(val);
      }
    };

    const addAadhaar = (value, label) => {
      const val = normalize(value);
      if (!val) return;
      if (aadhaarSet.has(val)) {
        errors.push(`Duplicate Aadhaar number found: ${label} (${val})`);
      } else {
        aadhaarSet.add(val);
      }
    };

    // ✅ Add main form numbers
    addMobile(mainData.mobile, "Main Mobile");
    addMobile(mainData.mobile2, "Main Mobile2");
    addAadhaar(mainData.aadhar, "Main Aadhaar");

    familyMembers.forEach((member, idx) => {
      const label = `Member ${idx + 1}`;
      if ((!member.mobile || member.mobile.trim() === "") && (!member.aadhar || member.aadhar.trim() === "")) {
        errors.push(`${label}: Mobile or Aadhar must be present`);
      }
      const m1 = normalize(member.mobile);
      const m2 = normalize(member.mobile2);

      if (m1 && m2 && m1 === m2) {
        errors.push(`${label}: Mobile and Mobile2 must be different`);
      }
      // Validate mobile number - must be 10 digits
      if (m1 && !/^\d{10}$/.test(m1)) {
        errors.push(`${label}: Mobile must be a 10-digit number`);
      }

      // Validate mobile2 number - must match the specified regex pattern
      if (m2 && !/^\+91[-]?\d{3}[-]?\d{3}[-]?\d{4}$|^(?:\+?\d{1,3})?[-.\s]?(\(?\d{2,3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(m2)) {
        errors.push(`${label}: Mobile2 must be a valid phone number (e.g., +91-123-456-7890)`);
      }

      // Validate Aadhaar number - must be 12 digits
      if (member.aadhar && !/^\d{12}$/.test(member.aadhar)) {
        errors.push(`${label}: Aadhaar number must be a 12-digit number`);
      }

      addMobile(m1, `${label} Mobile`);
      addMobile(m2, `${label} Mobile2`);
      addAadhaar(member.aadhar, `${label} Aadhaar`);
    });

    return errors;
  };


  const handleChange = async (fieldName) => {
    await trigger(fieldName);  // Trigger field validation
    clearErrors(fieldName);  // Clear error if valid
  };
  return (
    <>
      <Navbar />
      <Helmet>
        <title>ஸ்ரீ பொன்னாச்சி அம்மன் திருக்கோவில்கள் | குடும்ப வடிவம்</title>
        <link rel="canonical" href="/family/create" />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs /><br />
            <form onSubmit={(e) => { handleSubmit(handleFormSubmit)(e); }} className="auth_form" autoComplete="off">
              <h1>குடும்ப வடிவம்</h1>
              {/* Address Option Selection */}
              <div className="auth_element">
                <label className="auth_label">முகவரி வகையைத் தேர்வுசெய்யவும்</label>
                <br />
                <div className="auth_radio">
                  <label className="auth_radio-label">
                    <input
                      type="radio"
                      name="addressOption"
                      value="both"
                      checked={addressOption === "both"}
                      onChange={() => setAddressOption("both")}
                    />
                    தற்காலிக மற்றும் நிரந்தர முகவரிகள்
                  </label>
                  <br />
                  <label className="auth_radio-label">
                    <input
                      type="radio"
                      name="addressOption"
                      value="permanent"
                      checked={addressOption === "permanent"}
                      onChange={() => setAddressOption("permanent")}
                    />
                    நிரந்தர முகவரி மட்டும்
                  </label>
                </div>
              </div>


              <>
                {/* Address Line Inputs */}
                <label className="auth_label">நிரந்தர முகவரி</label>
                {/* Pincode Input */}
                <div className="auth_element">
                  <label className="auth_label">பின்கோடு</label>
                  <input
                    type="text"
                    value={permanentPincode}
                    onChange={(e) => setPermanentPincode(e.target.value)}
                    className="auth_input"
                    placeholder="பின்கோடை உள்ளிடவும்"
                    autoComplete="off"
                    inputMode="numeric"
                    maxLength="6"
                  />
                  <button type="button" onClick={loadPermanentAddressAreas} disabled={loadingPermanentPin}>
                    {loadingPermanentPin ? "ஏற்றுகிறது..." : "முகவரிப் பகுதிகளை ஏற்று"}
                  </button>
                  {permanentErrorMessage && <div className="auth_error">{permanentErrorMessage}</div>}
                  <br />
                </div>

                {/* Address Area Selection */}
                {permanentAddressAreas?.addressAreaList?.length > 0 && (
                  <div className="auth_element" style={{ marginBottom: '20px' }}>
                    <select
                      value={permanentSelectedAreaId || ""}
                      onChange={handleAreaChange}
                      className="auth_select"
                    >
                      <option value="" disabled>பகுதியைத் தேர்ந்தெடுக்கவும்</option>
                      {permanentAddressAreas.addressAreaList.map((area) => (
                        <option key={area.id} value={area.id}>
                          {`${area.name_tamil || area.name}, ${area.block_tamil || area.block}, ${area.district_tamil || area.district}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Subarea Selection */}
                {permanentSelectedAreaId && (
                  <div className="auth_element" style={{ marginTop: '20px' }}>
                    <label>ஊரைத் தேர்ந்தெடுக்கவும்:</label>
                    {permanentAddressAreas?.addressAreaList.find((area) => area.id === permanentSelectedAreaId)?.subAreas?.length > 0 ? (
                      <select
                        value={permanentSelectedSubareaId || ""}
                        onChange={handleSubareaChange}
                        className="auth_select"
                      >
                        <option value="" disabled>ஒரு ஊரைத் தேர்ந்தெடுக்கவும்</option>
                        {permanentAddressAreas.addressAreaList.find((area) => area.id === permanentSelectedAreaId).subAreas.map((subarea) => (
                          <option key={subarea.id} value={subarea.id}>
                            {(subarea.name_tamil || subarea.name)
                              ? (subarea.name_tamil || subarea.name).split('|').reverse().map((s) => s.trim()).join(', ')
                              : 'ஊர் கிடைக்கவில்லை'}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p>இந்தப் பகுதிக்கு எந்த இடமும் கிடைக்கவில்லை...</p>
                    )}
                  </div>
                )}
                {permanentSelectedSubareaId && (
                  <>
                    <div className="auth_element">
                      <label className="auth_label">நிரந்தர முகவரி கதவு எண்</label>
                      <input
                        type="text"
                        id="permanentAddressLine1"
                        {...register("permanentAddressLine1")}
                        className="auth_input"

                      />
                      {errors.permanentAddressLine1 && <div className="auth_error">❗ {errors.permanentAddressLine1.message}</div>}
                    </div>
                    <div className="auth_element">
                      <label className="auth_label">நிரந்தர முகவரி தெரு(Optional)</label>
                      <input
                        type="text"
                        id="permanentAddressLine2"
                        // value={addressData.permanent.addressLine2}
                        {...register("permanentAddressLine2")}
                        className="auth_input"

                      />
                      {errors.permanentAddressLine2 && <div className="auth_error">❗ {errors.permanentAddressLine2.message}</div>}
                    </div>
                    <div className="auth_element">
                      <label className="auth_label">நிரந்தர முகவரி கூடுதல் விவரம்(Optional)</label>
                      <input
                        type="text"
                        id="permanentAddressLine3"
                        // value={addressData.permanent.addressLine3}
                        {...register("permanentAddressLine3")}
                        className="auth_input"
                      />
                      {errors.permanentAddressLine3 && <div className="auth_error">❗ {errors.permanentAddressLine3.message}</div>}
                    </div>
                  </>
                )}
              </>
              {/* Temporary Address Selection (Only show if "Both" is selected) */}
              {addressOption === "both" && (
                <>
                  {/* Address Line Inputs */}
                  <hr />
                  <label className="auth_label">தற்காலிக முகவரி</label>
                  {/* Pincode Input */}
                  <div className="auth_element">
                    <label className="auth_label">பின்கோடு</label>
                    <input
                      type="text"
                      value={tempPincode}
                      onChange={(e) => setTempPincode(e.target.value)}
                      className="auth_input"
                      placeholder="பின்கோடை உள்ளிடவும்"
                      autoComplete="off"
                      inputMode="numeric"
                      maxLength="6"
                    />
                    <button type="button" onClick={loadTempAddressAreas} disabled={loadingTempPin}>
                      {loadingTempPin ? "ஏற்றுகிறது..." : "முகவரிப் பகுதிகளை ஏற்று"}
                    </button>
                    {tempErrorMessage && <div className="auth_error">{tempErrorMessage}</div>}
                    <br />
                  </div>

                  {/* Address Area Selection */}
                  {tempAddressAreas?.addressAreaList?.length > 0 && (
                    <div className="auth_element" style={{ marginBottom: '20px' }}>
                      <select
                        value={tempSelectedAreaId || ""}
                        onChange={handleTempAreaChange}
                        className="auth_select"
                      >
                        <option value="" disabled>பகுதியைத் தேர்ந்தெடுக்கவும்</option>
                        {tempAddressAreas.addressAreaList.map((area) => (
                          <option key={area.id} value={area.id}>
                            {`${area.name_tamil || area.name}, ${area.block_tamil || area.block}, ${area.district_tamil || area.district}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Subarea Selection */}
                  {tempSelectedAreaId && (
                    <div className="auth_element" style={{ marginTop: '20px' }}>
                      <label>ஊரைத் தேர்ந்தெடுக்கவும்:</label>
                      {tempAddressAreas?.addressAreaList.find((area) => area.id === tempSelectedAreaId)?.subAreas?.length > 0 ? (
                        <select
                          value={tempSelectedSubareaId || ""}
                          onChange={handleTempSubareaChange}
                          className="auth_select"
                        >
                          <option value="" disabled>ஒரு ஊரைத் தேர்ந்தெடுக்கவும்</option>
                          {tempAddressAreas.addressAreaList.find((area) => area.id === tempSelectedAreaId).subAreas.map((subarea) => (
                            <option key={subarea.id} value={subarea.id}>
                              {(subarea.name_tamil || subarea.name)
                                ? (subarea.name_tamil || subarea.name).split('|').reverse().map((s) => s.trim()).join(', ')
                                : 'ஊர் கிடைக்கவில்லை'}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p>இந்தப் பகுதிக்கு எந்த இடமும் கிடைக்கவில்லை...</p>
                      )}
                    </div>
                  )}
                  {tempSelectedSubareaId && (
                    <>
                      <div className="auth_element">
                        <label className="auth_label">தற்காலிக கதவு எண்</label>
                        <input
                          type="text"
                          id="tempAddressLine1"
                          //value={addressData.temp.addressLine1}
                          {...register("tempAddressLine1")}
                          className="auth_input"
                        />
                        {errors.tempAddressLine1 && <div className="auth_error">❗ {errors.tempAddressLine1.message}</div>}
                      </div>

                      <div className="auth_element">
                        <label className="auth_label">தற்காலிக முகவரி தெரு(Optional)</label>
                        <input
                          type="text"
                          id="tempAddressLine2"
                          // value={addressData.temp.addressLine2}
                          {...register("tempAddressLine2")}
                          className="auth_input"

                        />
                        {errors.tempAddressLine2 && <div className="auth_error">❗ {errors.tempAddressLine2.message}</div>}
                      </div>

                      <div className="auth_element">
                        <label className="auth_label">தற்காலிக முகவரி கூடுதல் விவரம்(Optional)</label>
                        <input
                          type="text"
                          id="tempAddressLine3"
                          //value={addressData.temp.addressLine3}
                          {...register("tempAddressLine3")}
                          className="auth_input"
                        />
                        {errors.tempAddressLine3 && <div className="auth_error">❗ {errors.tempAddressLine3.message}</div>}
                      </div>
                    </>
                  )}
                </>
              )}

              <hr></hr>
              {/* Adddress logic end */}
              <div className="auth_element">
                <label className="auth_label">விண்ணப்பப் படிவ எண்</label>
                <input
                  type="number"
                  id="applicationFormNumber"
                  {...register("applicationFormNumber", {
                    required: "இந்த கோப்பு பூர்த்தி செய்ய வேண்டும்", // Error message for required field
                    pattern: {
                      value: /^(?!1111$)\d{4}$/,
                      message: "சரியான 4 இலக்க எண் உள்ளிடவும் (1111 செல்லாது)",
                    },
                  })}
                  className="auth_input"
                  inputMode="numeric"
                  placeholder="1234"
                />
                {errors.applicationFormNumber && <div className="auth_error"> {errors.applicationFormNumber.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">கூட்டம்</label>
                <select
                  id="kootamName"
                  {...register("kootamName", { required: "கூட்டம் தேர்வு செய்ய வேண்டும்" })}
                  className="auth_select"
                >
                  <option value="">-- ஒரு கூட்டத்தைத் தேர்ந்தெடுக்கவும் --</option>
                  <option value="செம்பூத்தான்">Semboothan(செம்பூத்தான்)</option>
                  <option value="மணியன்">Manian(மணியன்)</option>
                  <option value="முத்தன்">Muthan(முத்தன்)</option>
                  <option value="பிற">பிற (கூட்டம்)</option>
                </select>
                {errors.kootamName && <div className="auth_error">❗ {errors.kootamName.message}</div>}

                {/* Custom Group Dropdown */}
                {watch("kootamName") === "பிற" && (
                  <div className="auth_element">
                    <label className="auth_label">பிற (கூட்டம்)</label>
                    <select
                      id="customGroup"
                      {...register("customGroup", {
                        required: "பிற (கூட்டம்) தேவை",
                      })}
                      className="auth_select"
                    >
                      <option value="">-- தேர்ந்தெடுக்கவும் --</option>
                      {customGroups.map((group) => (
                        <option key={group.value} value={group.value}>
                          {group.label}
                        </option>
                      ))}
                    </select>
                    {errors.customGroup && <div className="auth_error">❗ {errors.customGroup.message}</div>}
                  </div>
                )}

              </div>

              {/* Family Head Fields */}
              <div className="auth_element">
                <label className="auth_label">குடும்ப தலைவர் பெயர்(English)</label>
                <input
                  type="text"
                  id="tempFirstName"
                  value={nameData.tempFirstName || ""}
                  {...register("tempFirstName", {
                    required: "பெயர் தேவை",
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                    minLength: { value: 2, message: "குறைந்தது 2 எழுத்துகள் வேண்டும்" },
                  })}
                  className="auth_input"
                  placeholder="பொன்னாச்சி"
                  onChange={(e) => {
                    handleChange("tempFirstName");  // Trigger validation
                    handleNameChange("tempFirstName", e.target.value);  // Update state
                  }}
                />
                {errors.tempFirstName && <div className="auth_error">❗ {errors.tempFirstName.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">தந்தை/கணவர் பெயர்(English)</label>
                <input
                  type="text"
                  id="tempLastName"
                  value={nameData.tempLastName || ""}
                  {...register("tempLastName", {
                    required: "பெயர் தேவை",
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                    minLength: { value: 2, message: "குறைந்தது 2 எழுத்துகள் வேண்டும்" },
                  })}
                  className="auth_input"
                  placeholder="அம்மன்"
                  onChange={(e) => {
                    handleChange("tempLastName");  // Trigger validation
                    handleNameChange("tempLastName", e.target.value);  // Update state
                  }}
                />
                {errors.tempLastName && <div className="auth_error">❗ {errors.tempLastName.message}</div>}
              </div>
              <div className="auth_element">
                <label className="auth_label">குடும்ப பெயர்(English) (தேவையில்லை)</label>
                <input
                  type="text"
                  id="tempFamilyName"
                  value={nameData.tempFamilyName || ""}
                  {...register("tempFamilyName", {
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                  })}
                  className="auth_input"
                  placeholder="குடும்பம்"
                  onChange={(e) => {
                    handleChange("tempFamilyName");  // Trigger validation
                    handleNameChange("tempFamilyName", e.target.value);  // Update state
                  }}
                />
                {errors.tempFamilyName && <div className="auth_error">❗ {errors.tempFamilyName.message}</div>}
              </div>
              <div className="auth_element">
                <label className="auth_label">குடும்ப பூர்வீக ஊர்(English) (தேவையில்லை)</label>
                <input
                  type="text"
                  id="tempFamilyOrigin"
                  value={nameData.tempFamilyOrigin || ""}
                  {...register("tempFamilyOrigin", {
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                  })}
                  className="auth_input"
                  placeholder="பூர்வீக ஊர்"
                  onChange={(e) => {
                    handleChange("tempFamilyOrigin");  // Trigger validation
                    handleNameChange("tempFamilyOrigin", e.target.value);  // Update state
                  }}
                />
                {errors.tempFamilyOrigin && <div className="auth_error">❗ {errors.tempFamilyOrigin.message}</div>}
              </div>
              <button
                type="button"
                onClick={translateNameToTamil}
                disabled={loading}
              >
                தமிழில் மொழிபெயர்க்கவும்
              </button>
              <div className="auth_element">
                <label className="auth_label">குடும்ப தலைவர் பெயர்</label>
                <input
                  type="text"
                  id="firstName"
                  value={nameData.firstName || ""}
                  className="auth_input"
                  placeholder="பொன்னாச்சி"
                  onChange={(e) => {
                    // Update the nameData state when input changes
                    setNameData((prevData) => ({
                      ...prevData,
                      firstName: e.target.value,
                    }));
                    // If necessary, trigger re-validation or any additional logic here
                  }}
                />
                {errors.firstName && <div className="auth_error">❗ {errors.firstName.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">தந்தை/கணவர் பெயர்</label>
                <input
                  type="text"
                  id="lastName"
                  value={nameData.lastName || ""}
                  className="auth_input"
                  placeholder="அம்மன்"
                  onChange={(e) => {
                    setNameData((prevData) => ({
                      ...prevData,
                      lastName: e.target.value,
                    }));
                  }}
                />
                {errors.lastName && <div className="auth_error">❗ {errors.lastName.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">குடும்ப பெயர் (தேவையில்லை)</label>
                <input
                  type="text"
                  id="familyName"
                  value={nameData.familyName || ""}
                  {...register("familyName", {
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                  })}
                  className="auth_input"
                  placeholder="குடும்பம்"
                  onChange={(e) => {
                    setNameData((prevData) => ({
                      ...prevData,
                      familyName: e.target.value,
                    }));
                  }}
                />
                {errors.familyName && <div className="auth_error">❗ {errors.familyName.message}</div>}
              </div>
              <div className="auth_element">
                <label className="auth_label">குடும்ப பூர்வீக ஊர் (தேவையில்லை)</label>
                <input
                  type="text"
                  id="familyOrigin"
                  value={nameData.familyOrigin || ""}
                  {...register("familyOrigin", {
                    pattern: { value: /^[A-Za-z\u0B80-\u0BFF\s.,()]*$/, message: "எழுத்துக்கள், இடைவெளிகள் மற்றும் '.' மட்டும் அனுமதிக்கப்படும்" },
                  })}
                  className="auth_input"
                  placeholder="குடும்பம்"
                  onChange={(e) => {
                    setNameData((prevData) => ({
                      ...prevData,
                      familyOrigin: e.target.value,
                    }));
                  }}
                />
                {errors.familyOrigin && <div className="auth_error">❗ {errors.familyOrigin.message}</div>}
              </div>

              {/* Date of Birth */}
              <div className="auth_element">
                <label className="auth_label">பிறந்த தேதி</label>

                <div style={{ display: "flex", gap: "10px" }}>
                  {/* Year dropdown */}
                  <select
                    onChange={handleYearChange}
                    value={selectedYear}
                    className="auth_input"
                  >
                    <option value="">ஆண்டு தேர்வு செய்யவும்</option>
                    {getYearOptions(1940).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>

                  {/* Actual date input */}
                  <input
                    type="date"
                    id="dob"
                    ref={dateInputRef}
                    {...register("dob")}
                    className="auth_input"
                  />
                </div>

                {errors.dob && (
                  <div className="auth_error">❗ {errors.dob.message}</div>
                )}
              </div>

              {/* Aadhar Number Field */}
              <div className="auth_element">
                <label className="auth_label">ஆதார் எண்</label>
                <input
                  type="text"
                  id="aadhar"
                  {...register("aadhar", {
                    required: "ஆதார் எண் தேவை",
                    pattern: {
                      value: /^(\d{12}|\d{4} \d{4} \d{4})$/,
                      message: "12-இலக்க எண்ணாக இருக்க வேண்டும்,இடைவெளிகளைக் கொண்டிருக்கலாம்",
                    },
                  })}
                  className="auth_input"
                  placeholder="1234 5678 9012"
                  inputMode="numeric"
                />
                {errors.aadhar && <div className="auth_error">❗ {errors.aadhar.message}</div>}
              </div>

              {/* Mobile Number Field */}
              <div className="auth_element">
                <label className="auth_label">கைபேசி எண்</label>
                <input
                  type="tel"
                  id="mobile"
                  {...register("mobile", {
                    required: "கைபேசி எண் தேவை",
                    pattern: {
                      value: /^\d{10}$/,
                      message: "சரியான கைபேசி எண்ணை உள்ளிடவும்",
                    },
                  })}
                  className="auth_input"
                  placeholder="1234567890"
                  autoComplete="off"
                  inputMode="numeric"
                  maxLength="10"
                />
                {errors.mobile && <div className="auth_error"> {errors.mobile.message}</div>}
              </div>
              {/* Mobile Number Field */}
              <div className="auth_element">
                <label className="auth_label">கைபேசி எண் 2 (தேவையில்லை)</label>
                <input
                  type="tel"
                  id="mobile2"
                  {...register("mobile2", {

                    pattern: {
                      value: /^\+91[-]?\d{3}[-]?\d{3}[-]?\d{4}$|^(?:\+?\d{1,3})?[-.\s]?(\(?\d{2,3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
                      message: "சரியான கைபேசி எண்ணை உள்ளிடவும்",
                    },
                  })}
                  className="auth_input"
                  placeholder="+91-1234567890"
                />
                {errors.mobile2 && <div className="auth_error"> {errors.mobile2.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">மின்னஞ்சல்(தேவையில்லை)</label>
                <input
                  type="email"
                  id="email"
                  {...register("email", {
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+(?:[a-zA-Z0-9-]*[a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, // Improved regex to match most valid email formats
                      message: "மின்னஞ்சல் செல்லுபடியாகாது",
                    },
                  })}
                  className="auth_input"
                  placeholder="ponnachiamman@gmail.com"
                />
                {errors.email && <div className="auth_error">❗ {errors.email.message}</div>}
              </div>

              <div className="auth_element">
                <label className="auth_label">வணிக பெயர் (தேவையில்லை)</label>
                <select
                  id="businessName"
                  {...register("businessName")}
                  className="auth_select"
                >
                  <option value="">-- தேர்ந்தெடுக்கவும் --</option>
                  {businessNames.map((business) => (
                    <option key={business.value} value={business.value}>
                      {business.label}
                    </option>
                  ))}
                </select>
                {errors.businessName && <div className="auth_error">❗ {errors.businessName.message}</div>}
              </div>


              <div className="auth_element">
                <h2>குடும்ப உறுப்பினர்கள்</h2>
                {familyMembers.length > 0 ?
                  (
                    familyMembers.map((member, index) =>
                    (
                      <div key={index} className="auth_member_section">
                        <br />
                        <h3>உறுப்பினர் {index + 1}</h3>


                        <label className="auth_label">கைபேசி எண்</label>
                        <input
                          type="text"
                          placeholder="கைபேசி எண்"
                          value={member.mobile}
                          onChange={(e) => updateFamilyMember(index, 'mobile', e.target.value)}
                          className="auth_input"
                        />
                        {!/^\d{10}$/.test(member.mobile) && (
                          <div className="auth_error">❗ சரியான 10 இலக்க கைபேசி எண்ணை உள்ளிடவும்</div>
                        )}
                        <label className="auth_label">ஆதார் எண்</label>
                        <input
                          type="text"
                          placeholder="ஆதார் எண்"
                          value={member.aadhar}
                          onChange={(e) => updateFamilyMember(index, 'aadhar', e.target.value)}
                          className="auth_input"
                        />
                        {!/^(?!.*00$)(\d{12}|\d{4} \d{4} \d{4})$/.test(member.aadhar) && (
                          <div className="auth_error">❗ 12-இலக்க எண்ணாக இருக்க வேண்டும்</div>
                        )}

                        <label className="auth_label">கைபேசி எண் 2 (தேவையில்லை)</label>
                        <input
                          type="text"
                          placeholder="கைபேசி எண் 2"
                          value={member.mobile2}
                          onChange={(e) => updateFamilyMember(index, 'mobile2', e.target.value)}
                          className="auth_input"
                        />
                        {!/^\+91[-]?\d{3}[-]?\d{3}[-]?\d{4}$|^(?:\+?\d{1,3})?[-.\s]?(\(?\d{2,3}\)?)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(member.mobile2) && (
                          <div className="auth_error">❗ சரியான கைபேசி எண்ணை உள்ளிடவும்</div>
                        )}

                        <button
                          type="button"
                          onClick={() => removeFamilyMember(index)}
                          className="remove_member_btn"
                        >
                          🗑️ உறுப்பினரை நீக்கவும்
                        </button>
                      </div>
                    )
                    )
                  ) : (
                    <p>உறுப்பினர் சேர்க்க பொத்தானை அழுத்தவும்.</p>
                  )}

                <button type="button" onClick={addFamilyMember}>+ உறுப்பினர் சேர்க்கவும்</button>
              </div>
              <button type="submit" disabled={!isValid || isSubmitting}
                className={`auth_submit ${!isValid || isSubmitting ? "disabled_btn" : ""}`}
              >
                {loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பிக்கவும்'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default FamilyCreate;